import {LLMToolResultBlock, LLMToolCallBlock} from 'llm/chat-library/messages/MessageTypes';
import {ProgressToolCall} from 'llm/deep-research/tool-details/types';

import {
  DeepResearchToolType,
  ToolCallStatus,
  SearchToolCall,
  GetWarehouseOverviewToolCall,
  QueryWarehouseToolCall,
  ReadBenchlingDocumentToolCall,
  SearchNotebookEntriesToolCall,
  SearchSequencesToolCall,
  AttachmentToolCall,
  BaseProgressToolCall,
  PublicDataSubAgentToolCall,
  IdsToModelsToolCall,
} from './types';

// Utility function to extract text content from tool result
function extractResultText(toolResult: LLMToolResultBlock): string {
  return toolResult.value.content
    .filter((content) => content.type === 'text')
    .map((content) => {
      const textBlock = content;
      if (textBlock.value.text) {
        return textBlock.value.text;
      }
      return '';
    })
    .join(' ')
    .trim();
}

// Base function to create common tool call properties
function createBaseToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date,
  chipName: string
): BaseProgressToolCall {
  const resultText = toolResultBlock ? extractResultText(toolResultBlock) : undefined;

  return {
    tool: toolCallBlock.value.name,
    toolId: toolCallBlock.value.id,
    createdAt,
    status: toolResultBlock ? ToolCallStatus.COMPLETED : ToolCallStatus.PENDING,
    chipName,
    responseText: resultText,
  };
}

// Individual tool call parsers
function parseSearchToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): SearchToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt, 'Search');

  return {
    ...baseCall,
    tool: DeepResearchToolType.SearchTool,
    query: (toolCallBlock.value.args.query as string) || '',
    objectTypes: (toolCallBlock.value.args.objectTypes as Array<string>) || [],
  };
}

function parseSearchNotebookEntriesToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): SearchNotebookEntriesToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt, 'Search Entries');

  return {
    ...baseCall,
    tool: DeepResearchToolType.SearchNotebookEntries,
    query: (toolCallBlock.value.args.search_queries as string) || '',
  };
}

function parseSearchSequencesToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): SearchSequencesToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt, 'Search Sequences');

  return {
    ...baseCall,
    tool: DeepResearchToolType.SearchSequencesTool,
    query: (toolCallBlock.value.args.query as string) || '',
  };
}

function parseGetWarehouseOverviewToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): GetWarehouseOverviewToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt, 'Warehouse Overview');

  return {
    ...baseCall,
    tool: DeepResearchToolType.GetWarehouseOverviewTool,
  };
}

function parseQueryWarehouseToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): QueryWarehouseToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt, 'Query Warehouse');

  return {
    ...baseCall,
    tool: DeepResearchToolType.QueryWarehouseTool,
    query: (toolCallBlock.value.args.query as string) || '',
  };
}

function parseNotebookQueryToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): ReadBenchlingDocumentToolCall {
  const baseCall = createBaseToolCall(
    toolCallBlock,
    toolResultBlock,
    createdAt,
    (toolCallBlock.value.args.notebook_id as string) || 'Read Notebook'
  );

  return {
    ...baseCall,
    tool: DeepResearchToolType.ReadBenchlingDocumentTool,
    notebookId: (toolCallBlock.value.args.notebook_id as string) || '',
  };
}

function parseAttachmentToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): AttachmentToolCall {
  const baseCall = createBaseToolCall(
    toolCallBlock,
    toolResultBlock,
    createdAt,
    (toolCallBlock.value.args.attachmentId as string) || 'Attachment'
  );

  return {
    ...baseCall,
    tool: DeepResearchToolType.AttachmentTool,
    attachmentId: (toolCallBlock.value.args.attachmentId as string) || '',
  };
}

function parsePublicDataSubAgentToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): PublicDataSubAgentToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt, 'Public Data Agent');

  return {
    ...baseCall,
    tool: DeepResearchToolType.PublicDataSubAgent,
    query: (toolCallBlock.value.args.query as string) || '',
  };
}

function parseIdsToModelsToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): IdsToModelsToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt, 'Ids to Models');

  return {
    ...baseCall,
    chipName: 'Inspect Details',
    tool: DeepResearchToolType.IdsToModelsTool,
    objectIds: (toolCallBlock.value.args.object_ids as Array<string>) || [],
    includeRelevantItems: (toolCallBlock.value.args.include_relevant_items as boolean) || false,
  };
}

function parseGenericToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): BaseProgressToolCall {
  return createBaseToolCall(toolCallBlock, toolResultBlock, createdAt, 'Tool');
}

// Tool parser registry
const toolParsers: Record<
  DeepResearchToolType,
  (
    toolCallBlock: LLMToolCallBlock,
    toolResultBlock: LLMToolResultBlock | null,
    createdAt: Date
  ) => ProgressToolCall
> = {
  [DeepResearchToolType.SearchTool]: parseSearchToolCall,
  [DeepResearchToolType.SearchNotebookEntries]: parseSearchNotebookEntriesToolCall,
  [DeepResearchToolType.SearchSequencesTool]: parseSearchSequencesToolCall,
  [DeepResearchToolType.GetWarehouseOverviewTool]: parseGetWarehouseOverviewToolCall,
  [DeepResearchToolType.QueryWarehouseTool]: parseQueryWarehouseToolCall,
  [DeepResearchToolType.ReadBenchlingDocumentTool]: parseNotebookQueryToolCall,
  [DeepResearchToolType.AttachmentTool]: parseAttachmentToolCall,
  [DeepResearchToolType.ResearchSubAgent]: parseGenericToolCall,
  [DeepResearchToolType.Think]: parseGenericToolCall,
  [DeepResearchToolType.CompleteAnswer]: parseGenericToolCall,
  [DeepResearchToolType.PublicDataSubAgent]: parsePublicDataSubAgentToolCall,
  [DeepResearchToolType.IdsToModelsTool]: parseIdsToModelsToolCall,
};

/**
 * Main function to convert a tool call and optional result into a ProgressToolCall
 */
export function getToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): ProgressToolCall | null {
  const toolName = toolCallBlock.value.name;
  const parser = toolParsers[toolName];

  if (!parser) {
    return null;
  }

  return parser(toolCallBlock, toolResultBlock, createdAt);
}
